import { Language } from '@zep/types/common';

import { CMS_CDN } from './env';

/**
 * Zepetoful 랜딩 페이지 이미지 URL 맵
 *
 * - locale별 분기가 필요한 이미지: 키 이름에 '-localized' suffix 사용
 * - 공통 이미지: 단일 URL 문자열 사용
 *
 * @example
 * // locale별 이미지
 * 'zep-feature-meeting-localized': {
 *   ko: `${CMS_CDN}/.../meeting-ko.png`,
 *   en: `${CMS_CDN}/.../meeting-en.png`,
 *   ja: `${CMS_CDN}/.../meeting-ja.png`,
 * }
 *
 * // 공통 이미지 (모든 locale에서 동일)
 * 'zep-app-icon': `${CMS_CDN}/.../app-icon.png'
 */

type LocalizedImageUrl = Partial<Record<Language, string>> & { en: string };
type CommonImageUrl = string;
type ImageUrl = LocalizedImageUrl | CommonImageUrl;

export const LANDING_IMAGES: Record<string, ImageUrl> = {
  // ZEP 서비스 이미지
  'zep-feature-meeting': `${CMS_CDN}/medias/9z7bCJzn/OmL/6S1sv3Wk87CV3B3oN71wOmL/en-US/1/core_1.webp`,
  'zep-feature-communication-localized': {
    ko: `${CMS_CDN}/medias/9z7bCJzn/oPK/1Yr9o3Wk87DV3B3oN71noPK/en-US/1/core_2_kr.webp`,
    en: `${CMS_CDN}/medias/9z7bCJzn/igM/7BbX83Wk87DV3B3oN91ligM/en-US/1/core_2_en.webp`,
    ja: `${CMS_CDN}/medias/9z7bCJzn/fZD/1s76V3Wk87DV3B3oN81lfZD/en-US/1/core_2_jp.webp`,
  },
  'zep-feature-templates': `${CMS_CDN}/medias/9z7bCJzn/vUU/1hPRl3Wk87CV3B3oN76RvUU/en-US/1/core_3.webp`,
  'zep-feature-editor': `${CMS_CDN}/medias/9z7bCJzn/zJF/1wSOm3Wk87CV3B3oN81pzJF/en-US/1/core_4.webp`,

  // ZEP QUIZ 서비스 이미지
  'quiz-feature-1-localized': {
    ko: `${CMS_CDN}/medias/9z7bCJzn/xdq/1lh7M3Wk87DV3B3zPh6Rxdq/en-US/1/quiz_1_kr.webp`,
    en: `${CMS_CDN}/medias/9z7bCJzn/paf/6Rzle3Wk87DV3B3zPg1Ypaf/en-US/1/quiz_1_en.webp`,
    ja: `${CMS_CDN}/medias/9z7bCJzn/rCp/1q0L43Wk87CV3B3zPh6RrCp/en-US/1/quiz_1_jp.webp`,
  },
  'quiz-feature-2-localized': {
    ko: `${CMS_CDN}/medias/9z7bCJzn/RN4/1hNu03Wk87CV3B3zPh1wRN4/en-US/1/quiz_2_kr.webp`,
    en: `${CMS_CDN}/medias/9z7bCJzn/vRN/1npx53Wk87CV3B3zPg6RvRN/en-US/5/quiz_2_en.webp`,
    ja: `${CMS_CDN}/medias/9z7bCJzn/tNC/6Rp1u3Wk87DV3B3zPh6RtNC/en-US/1/quiz_2_jp.webp`,
  },
  'quiz-feature-3': `${CMS_CDN}/medias/9z7bCJzn/rWT/1lifc3Wk87CV3B4Aji1nrWT/en-US/1/quiz_3.webp`,
  'quiz-feature-4-localized': {
    ko: `${CMS_CDN}/medias/9z7bCJzn/xbF/1ljip3Wk87DV3B4Ajf6RxbF/en-US/1/quiz_4_kr.webp`,
    en: `${CMS_CDN}/medias/9z7bCJzn/sBW/PWnph3Wk87DV3B4Aje1YsBW/en-US/1/quiz_4_en.webp`,
    ja: `${CMS_CDN}/medias/9z7bCJzn/zni/6Rp3z3Wk87CV3B4Ajm6Rzni/en-US/1/quiz_4_jp.webp`,
  },

  // ZEP SCHOOL 서비스 이미지
  'school-feature-1-localized': {
    ko: `${CMS_CDN}/medias/9z7bCJzn/mwh/1lhdH3Wk87DV3B4KR06Rmwh/en-US/1/school_1_kr.webp`,
    en: `${CMS_CDN}/medias/9z7bCJzn/vUU/6Rmta3Wk87CV3B4Ajl6RvUU/en-US/1/school_1_en.webp`,
    ja: `${CMS_CDN}/medias/9z7bCJzn/7cv/6RvRN3Wk87DV3B4Ajd1s7cv/en-US/1/school_1_jp.webp`,
  },
  'school-feature-2-localized': {
    ko: `${CMS_CDN}/medias/9z7bCJzn/IWi/6RrCJ3Wk87CV3B4KR01uIWi/en-US/1/school_2_kr.webp`,
    en: `${CMS_CDN}/medias/9z7bCJzn/1wY/6Rzl83Wk87CV3B4KR26S1wY/en-US/1/school_2_en.webp`,
    ja: `${CMS_CDN}/medias/9z7bCJzn/vUU/1wRtF3Wk87DV3B4KR36RvUU/en-US/1/school_2_jp.webp`,
  },
  'school-feature-3': `${CMS_CDN}/medias/9z7bCJzn/sBt/6RrBm3Wk87DV3B4KR21YsBt/en-US/1/school_3.webp`,
  'school-feature-4-localized': {
    ko: `${CMS_CDN}/medias/9z7bCJzn/rDL/6RmsZ3Wk87DV3B4Pgt6RrDL/en-US/1/school_4_kr.webp`,
    en: `${CMS_CDN}/medias/9z7bCJzn/X62/1nrWE3Wk87CV3B4KR21yX62/en-US/1/school_4_en.webp`,
    ja: `${CMS_CDN}/medias/9z7bCJzn/vSP/1uI1I3Wk87CV3B4Pgs6RvSP/en-US/1/school_4_jp.webp`,
  },
};

export type LandingImageKey = keyof typeof LANDING_IMAGES;

/**
 * 이미지 키가 locale별 분기가 필요한지 확인
 */
export function isLocalizedImage(imageKey: LandingImageKey): boolean {
  return imageKey.endsWith('-localized');
}

/**
 * locale에 맞는 이미지 URL 반환
 * locale별 이미지가 없으면 영어 이미지 URL 반환
 */
export function getLandingImageUrl(
  imageKey: LandingImageKey,
  locale: Language,
): string {
  const imageData = LANDING_IMAGES[imageKey];

  if (typeof imageData === 'string') {
    return imageData;
  }
  return imageData[locale] ?? imageData.en;
}
